import { FC, ReactNode } from "react"

import { classNames } from "../../utils"
import { STATS_PALETTE } from "../stats/theme"

interface GradientProgressRowProps {
  label: ReactNode
  count: number
  maxCount: number
  netPercentage?: string
  isPositive?: boolean
  isActive?: boolean
  onClick?: () => void
  color?: string
}

const GradientProgressRow: FC<GradientProgressRowProps> = ({
  label,
  count,
  maxCount,
  netPercentage,
  isPositive,
  isActive,
  onClick,
  color = STATS_PALETTE[0]
}) => {
  const widthPercentage = maxCount ? Math.min((count / maxCount) * 100, 100) : 0

  const content = (
    <>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 text-sm font-medium text-base-dark">
          <span className="block truncate">{label}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-sm font-semibold tabular-nums text-base-dark">
            {count?.toLocaleString("en-US")}
          </span>
          {netPercentage && (
            <span
              title="Change from the previous period"
              className={`rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${
                isPositive
                  ? "bg-green-50 text-green-700"
                  : "bg-red-50 text-red-700"
              }`}
            >
              {netPercentage}
            </span>
          )}
        </div>
      </div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full motion-safe:transition-[width] motion-safe:duration-300"
          style={{
            width: `${widthPercentage <= 0 ? 0 : widthPercentage}%`,
            backgroundColor: color
          }}
        />
      </div>
    </>
  )

  // A row that filters the page is a real button: focusable, and it reports
  // whether its filter is on.
  if (onClick) {
    return (
      <button
        type="button"
        aria-pressed={Boolean(isActive)}
        onClick={onClick}
        className={classNames(
          "-m-2 flex min-w-0 flex-col rounded-lg p-2 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-200",
          isActive ? "bg-primary-50" : "hover:bg-slate-50"
        )}
      >
        {content}
      </button>
    )
  }

  return <div className="flex min-w-0 flex-col">{content}</div>
}

export default GradientProgressRow
